/**
 * Programme Store (Zustand)
 *
 * Loads registered programmes from API, groups by category.
 * Permission-filtered server-side — only returns what the user can see.
 *
 * Zusaetzlich: Liste aller aktiven User als Owner-Auswahl im Dropdown der
 * ProgramCard. Wird einmal beim Laden des Dashboards befuellt, neu eingeladene
 * User tauchen beim naechsten loadProgrammes() automatisch auf.
 */

import { create } from 'zustand';
import { api, ProgrammeInfo } from '../services/api';

export interface CategoryGroup {
  name: string;
  icon: string;
  order: number;
  programmes: ProgrammeInfo[];
}

export interface AssignableUser {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}

interface ProgrammeState {
  programmes: ProgrammeInfo[];
  categories: CategoryGroup[];
  assignableUsers: AssignableUser[];
  isLoading: boolean;

  loadProgrammes: () => Promise<void>;
  getProgramme: (id: string) => ProgrammeInfo | undefined;
  getCategoryProgrammes: (category: string) => ProgrammeInfo[];
}

function groupByCategory(programmes: ProgrammeInfo[]): CategoryGroup[] {
  const map = new Map<string, CategoryGroup>();

  for (const prog of programmes) {
    let group = map.get(prog.category);
    if (!group) {
      group = {
        name: prog.category,
        icon: prog.categoryIcon,
        order: prog.categoryOrder,
        programmes: [],
      };
      map.set(prog.category, group);
    }
    group.programmes.push(prog);
  }

  // Sort categories by order, programmes within category by programOrder
  const groups = Array.from(map.values());
  groups.sort((a, b) => a.order - b.order);
  for (const g of groups) {
    g.programmes.sort((a, b) => a.programOrder - b.programOrder);
  }

  return groups;
}

export const useProgrammeStore = create<ProgrammeState>((set, get) => ({
  programmes: [],
  categories: [],
  assignableUsers: [],
  isLoading: false,

  loadProgrammes: async () => {
    set({ isLoading: true });
    try {
      // Programme + User parallel laden. User-Liste-Fehler ist nicht fatal —
      // dann gibt's nur kein Dropdown auf den Cards, Programme zeigen aber.
      const [programmes, usersRaw] = await Promise.all([
        api.getProgrammes(),
        api.users.list().catch(() => [] as any[]),
      ]);
      const assignableUsers: AssignableUser[] = (usersRaw as Array<{
        id: number; firstName: string; lastName: string; email: string; isActive: boolean;
      }>)
        .filter((u) => u.isActive)
        .map((u) => ({ id: u.id, firstName: u.firstName, lastName: u.lastName, email: u.email }))
        .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));

      set({
        programmes,
        categories: groupByCategory(programmes),
        assignableUsers,
        isLoading: false,
      });
    } catch {
      set({ isLoading: false });
    }
  },

  getProgramme: (id) => get().programmes.find((p) => p.id === id),

  getCategoryProgrammes: (category) =>
    get().programmes.filter((p) => p.category === category),
}));
